import { isNumber } from "@foretagsplatsen/core-helpers/number";
import pluralDeclensionRules from "../pluralDeclensionRules.js";
import PolyglotObject from "./PolyglotObject.js";

const defaultLanguageCode = "en-US";

/**
 * Translation framework : The polyglot object holds the current
 * language and is globally available through the window object.
 */
export class Polyglot {
	constructor() {
		// a Map of locale code to Locale instance:
		this._locales = undefined;
		this._currentLocale = undefined;
	}

	setLocales(locales) {
		this._locales = locales;

		if (!this._currentLocale) {
			this.setLanguage(defaultLanguageCode);
		}
	}

	getLanguage() {
		return this._currentLocale.getCode();
	}

	setLanguage(lang) {
		this._currentLocale =
			this._getLocaleForLanguage(lang) ||
			this._getLocaleForLanguage(defaultLanguageCode);
	}

	translateServerString(source, replacementMap) {
		return this._translate(source, replacementMap);
	}

	withLanguage(newLanguage, callback) {
		const previousLocale = this._currentLocale;

		this.setLanguage(newLanguage);

		try {
			callback();
		} finally {
			this._currentLocale = previousLocale;
		}
	}

	// Input arguments are (declensions... , numberOfItems, replacementMap)
	_translatePluralDeclension(...args) {
		const declensionsLength = isNumber(args.at(-1))
			? args.length - 1
			: args.length - 2;
		const numberOfItems = args[declensionsLength];
		const language = this._currentLocale.getCode();
		const declensionIndex = this._getDeclensionIndex(
			numberOfItems,
			language,
		);

		if (declensionIndex > declensionsLength - 1) {
			throw new Error(`Wrong number of arguments for ${language}`);
		}

		const source = args[declensionIndex];
		const replacementMap = args[declensionsLength + 1];

		return this._translateSingleDeclension(source, replacementMap);
	}

	_getDeclensionIndex(numberOfItems, language) {
		let rule = pluralDeclensionRules[language];
		rule ||= pluralDeclensionRules["default"];
		return rule(numberOfItems);
	}

	_translateSingleDeclension(source, replacementMap) {
		return new PolyglotObject({
			translation: () =>
				this._translateWithLocale(
					this._currentLocale,
					source,
					replacementMap,
				),
			hyphenate: (text) => this._hyphenate(text, this._currentLocale),
			rawValue: source,
		});
	}

	_translate(...args) {
		// We have singular / plural difference
		// We assume this second argument is either a string or a map
		if (typeof args[1] === "string") {
			return this._translatePluralDeclension.apply(this, args);
		}
		return this._translateSingleDeclension.apply(this, args);
	}

	_translateWithLocale(locale, source, replacementMap) {
		const translation =
			this._translationForLocale(source, locale) || source;
		return this._replaceTranslationMatches(translation, replacementMap);
	}

	_replaceTranslationMatches(translation, replacementMap) {
		// Tries to match any template: {variable} or {variable: text}
		// eslint-disable-next-line sonarjs/slow-regex -- this is safe as we only translate literal strings
		const matches = /({[^}]+})/.exec(translation);
		if (!matches) {
			return translation;
		}

		const match = matches[1];
		const split = translation.split(match);
		const before = split[0];
		split.splice(0, 1);
		const rest = split.join(match);
		const replacedMatch = this._replaceTranslationMatch(
			match,
			replacementMap,
		);
		const after = this._replaceTranslationMatches(rest, replacementMap);
		return [before, replacedMatch, after];
	}

	_replaceTranslationMatch(text, replacementMap) {
		return (
			this._replaceWithRenderer(text, replacementMap) ||
			this._replaceWithValue(text, replacementMap)
		);
	}

	_replaceWithRenderer(text, replacementMap) {
		// Tries to match {variable: text}
		// eslint-disable-next-line sonarjs/slow-regex -- don't want to change that now
		const match = /^{(\w+):\s*([^}]+)\s*}$/.exec(text);

		if (!match) {
			return null;
		}

		const key = match[1];
		const value = match[2];
		const renderer = replacementMap[key];

		if (!renderer) {
			throw new Error(
				`Translation error: No replacement found for ${key}`,
			);
		}

		if (typeof renderer === "function") {
			return renderer(value);
		}

		throw new Error(`Translation error: invalid replacement for ${key}`);
	}

	_replaceWithValue(text, replacementMap) {
		// Tries to match {variable}
		const regex = /{(\w+)}/;
		const match = regex.exec(text);
		if (!match) {
			return text;
		}
		const key = match[1];
		const value = replacementMap[key];
		if (value === undefined) {
			throw new Error(
				`Translation error: No replacement found for ${key}`,
			);
		}

		return value;
	}

	_hyphenate(object, locale) {
		return locale.hyphenate(object);
	}

	_translationForLocale(source, locale) {
		return locale.getTranslation(source);
	}

	_getLocaleForLanguage(localeCode) {
		return this._locales.get(localeCode);
	}
}

const polyglot = new Polyglot();

export const _ = polyglot._translate.bind(polyglot);

export default polyglot;
